import React from 'react'
import { Box, Chip, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {
  BacktestFilterType,
  backtestFilterDefaultValues,
} from '../../../contexts/ApplicationSuiteProvider'

interface FilterDisplayProps {
  backtestFilters: BacktestFilterType
  setBacktestFilters: (filters: BacktestFilterType) => void
  showLabel?: boolean
}

function FilterDisplay({
  backtestFilters,
  setBacktestFilters,
  showLabel = true,
}: FilterDisplayProps) {
  const getActiveFilters = () => {
    const activeFilters: Array<{ label: string; filterKeys: (keyof BacktestFilterType)[] }> = []

    // Symbol filter
    if (
      backtestFilters.symbolSearch &&
      backtestFilters.symbolSearch !== backtestFilterDefaultValues.symbolSearch
    ) {
      activeFilters.push({
        label: `Symbol: ${backtestFilters.symbolSearch}`,
        filterKeys: ['symbolSearch'],
      })
    }

    // Pattern name search
    if (
      backtestFilters.patternNameSearch &&
      backtestFilters.patternNameSearch !== backtestFilterDefaultValues.patternNameSearch
    ) {
      activeFilters.push({
        label: `Pattern: ${backtestFilters.patternNameSearch}`,
        filterKeys: ['patternNameSearch'],
      })
    }

    // Profit percentage range
    if (
      backtestFilters.minProfitPercentage !== backtestFilterDefaultValues.minProfitPercentage ||
      backtestFilters.maxProfitPercentage !== backtestFilterDefaultValues.maxProfitPercentage
    ) {
      const min =
        backtestFilters.minProfitPercentage === Number.MIN_SAFE_INTEGER
          ? 'Any'
          : `${backtestFilters.minProfitPercentage}%`
      const max =
        backtestFilters.maxProfitPercentage === Number.MAX_SAFE_INTEGER
          ? 'Any'
          : `${backtestFilters.maxProfitPercentage}%`
      activeFilters.push({
        label: `Profit: ${min} - ${max}`,
        filterKeys: ['minProfitPercentage', 'maxProfitPercentage'],
      })
    }

    // Profit dollars range
    if (
      backtestFilters.minProfitDollars !== backtestFilterDefaultValues.minProfitDollars ||
      backtestFilters.maxProfitDollars !== backtestFilterDefaultValues.maxProfitDollars
    ) {
      const min =
        backtestFilters.minProfitDollars === Number.MIN_SAFE_INTEGER
          ? 'Any'
          : `$${backtestFilters.minProfitDollars.toLocaleString()}`
      const max =
        backtestFilters.maxProfitDollars === Number.MAX_SAFE_INTEGER
          ? 'Any'
          : `$${backtestFilters.maxProfitDollars.toLocaleString()}`
      activeFilters.push({
        label: `Profit $: ${min} - ${max}`,
        filterKeys: ['minProfitDollars', 'maxProfitDollars'],
      })
    }

    // Absolute drawdown range
    if (
      backtestFilters.minAbsoluteDrawdownPercentage !==
        backtestFilterDefaultValues.minAbsoluteDrawdownPercentage ||
      backtestFilters.maxAbsoluteDrawdownPercentage !==
        backtestFilterDefaultValues.maxAbsoluteDrawdownPercentage
    ) {
      const min =
        backtestFilters.minAbsoluteDrawdownPercentage === Number.MIN_SAFE_INTEGER
          ? 'Any'
          : `${backtestFilters.minAbsoluteDrawdownPercentage}%`
      const max =
        backtestFilters.maxAbsoluteDrawdownPercentage === Number.MAX_SAFE_INTEGER
          ? 'Any'
          : `${backtestFilters.maxAbsoluteDrawdownPercentage}%`
      activeFilters.push({
        label: `Abs DD: ${min} - ${max}`,
        filterKeys: ['minAbsoluteDrawdownPercentage', 'maxAbsoluteDrawdownPercentage'],
      })
    }

    // Relative drawdown range
    if (
      backtestFilters.minRelativeDrawdownPercentage !==
        backtestFilterDefaultValues.minRelativeDrawdownPercentage ||
      backtestFilters.maxRelativeDrawdownPercentage !==
        backtestFilterDefaultValues.maxRelativeDrawdownPercentage
    ) {
      const min =
        backtestFilters.minRelativeDrawdownPercentage === Number.MIN_SAFE_INTEGER
          ? 'Any'
          : `${backtestFilters.minRelativeDrawdownPercentage}%`
      const max =
        backtestFilters.maxRelativeDrawdownPercentage === Number.MAX_SAFE_INTEGER
          ? 'Any'
          : `${backtestFilters.maxRelativeDrawdownPercentage}%`
      activeFilters.push({
        label: `Rel DD: ${min} - ${max}`,
        filterKeys: ['minRelativeDrawdownPercentage', 'maxRelativeDrawdownPercentage'],
      })
    }

    // Date range
    if (
      backtestFilters.minDateRange !== backtestFilterDefaultValues.minDateRange ||
      backtestFilters.maxDateRange !== backtestFilterDefaultValues.maxDateRange
    ) {
      const min =
        backtestFilters.minDateRange === Number.MIN_SAFE_INTEGER
          ? 'Any'
          : `${new Date(backtestFilters.minDateRange).toLocaleDateString()}`
      const max =
        backtestFilters.maxDateRange === Number.MAX_SAFE_INTEGER
          ? 'Any'
          : `${new Date(backtestFilters.maxDateRange).toLocaleDateString()}`
      activeFilters.push({
        label: `Date Range: ${min} - ${max}`,
        filterKeys: ['minDateRange', 'maxDateRange'],
      })
    }

    // Days between
    if (
      backtestFilters.daysBetween &&
      backtestFilters.daysBetween !== backtestFilterDefaultValues.daysBetween
    ) {
      activeFilters.push({
        label: `Days Between: ${backtestFilters.daysBetween}`,
        filterKeys: ['daysBetween'],
      })
    }

    // Trigger chart filters
    if (backtestFilters.triggerChart) {
      if (backtestFilters.triggerChart.periodicity && backtestFilters.triggerChart.timeframe) {
        activeFilters.push({
          label: `Trigger Chart: ${backtestFilters.triggerChart.periodicity} ${backtestFilters.triggerChart.timeframe}`,
          filterKeys: ['triggerChart'],
        })
      }
    }

    return activeFilters
  }

  const activeFilters = getActiveFilters()

  if (activeFilters.length === 0) {
    return null
  }

  const removeFilter = (filterKeys: (keyof BacktestFilterType)[]) => {
    const updatedFilters = { ...backtestFilters }
    filterKeys.forEach((key) => {
      ;(updatedFilters as any)[key] = backtestFilterDefaultValues[key]
    })
    setBacktestFilters(updatedFilters)
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {showLabel && (
        <Typography variant="caption" color="text.secondary">
          Filters:
        </Typography>
      )}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={{ xs: 0, sm: 1, md: 0 }}>
        {activeFilters.map((filter, index) => (
          <Chip
            key={index}
            label={filter.label}
            size="small"
            variant="outlined"
            deleteIcon={<CloseIcon />}
            onDelete={() => removeFilter(filter.filterKeys)}
            sx={{
              fontSize: '0.75rem',
              height: '24px',
              '& .MuiChip-label': {
                px: 1,
              },
              '& .MuiChip-deleteIcon': {
                fontSize: '0.875rem',
                color: 'text.secondary',
              },
              '&:hover .MuiChip-deleteIcon': {
                color: 'error.main',
              },
            }}
            onClick={() => removeFilter(filter.filterKeys)}
          />
        ))}
      </Box>
    </Box>
  )
}

export default FilterDisplay
